import { ChangeDetectionStrategy, Component, ElementRef, input, output, viewChildren } from '@angular/core';
import { LocalizedString } from '@cue/api';
import { InstructionItem } from '@cue/data';
import { cueArrowRight, cueCircle, cueMarker, cueStart, IconComponent } from '@cue/ui/icons';
import { ButtonComponent } from '@cue/ui';
import { LocalizedStringPipe } from '@cue/utilities';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { NavigationBarDataV2 } from '../../pipes/navigation-bar.pipe';

export interface SpecificAreaDataCrate {
  areaId: number;
  index: number;
}

export interface RouteOverviewNote {
  number: number;
  instruction: InstructionItem;
  instructionIndex: number;
  title?: LocalizedString | null;
  description?: LocalizedString | null;
}

export interface RouteOverviewNoteSelection extends SpecificAreaDataCrate {
  instruction: InstructionItem;
  instructionIndex: number;
}

@Component({
  selector: 'assist-navigation-route-overview',
  templateUrl: './navigation-route-overview.component.html',
  styleUrl: './navigation-route-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, IconComponent, LocalizedStringPipe],
  host: {
    '[class.vertical]': 'layout() === "vertical"',
  },
})
export class NavigationRouteOverviewComponent {
  instructionsToScroll = viewChildren('instructionsToScroll', {
    read: ElementRef,
  });

  navBarData = input<NavigationBarDataV2 | null>();
  areaId = input<number>();
  groupIdx = input.required<number>();
  layout = input<'horizontal' | 'vertical'>('horizontal');
  showNotes = input(false);
  routeOverviewNotes = input<Record<number, RouteOverviewNote[]>>({});

  requestMoveToSpecificArea = output<SpecificAreaDataCrate>();
  routeOverviewNoteClick = output<RouteOverviewNoteSelection>();

  scrollToGroup = explicitEffect([this.groupIdx, this.instructionsToScroll], ([groupIdx]) => {
    if (this.instructionsToScroll().length > groupIdx) {
      const foundInstructionEle = this.instructionsToScroll().find(
        (x) => x.nativeElement.attributes['data-groupIdx']?.value == groupIdx.toString()
      );
      if (foundInstructionEle) {
        foundInstructionEle.nativeElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
  });

  protected ICONS = {
    cueArrowRight,
    cueCircle,
    cueMarker,
    cueStart,
  };

  protected moveToArea(areaId: number, index: number) {
    this.requestMoveToSpecificArea.emit({
      areaId,
      index,
    });
  }

  protected moveToHiddenArea(data: NavigationBarDataV2, index: number) {
    const item = data.areaList[index];

    if (item) {
      this.moveToArea(item.areaId, index);
    }
  }

  protected selectRouteOverviewNote(areaId: number, areaIndex: number, note: RouteOverviewNote) {
    this.routeOverviewNoteClick.emit({
      areaId,
      index: areaIndex,
      instruction: note.instruction,
      instructionIndex: note.instructionIndex,
    });
  }

  protected selectHiddenRouteOverviewNote(data: NavigationBarDataV2, areaIndex: number, note: RouteOverviewNote) {
    const item = data.areaList[areaIndex];

    if (item) {
      this.selectRouteOverviewNote(item.areaId, areaIndex, note);
    }
  }

  protected notesForArea(areaIndex: number): RouteOverviewNote[] {
    return this.routeOverviewNotes()[areaIndex] ?? [];
  }

  protected hasNotes(areaIndex: number): boolean {
    return this.notesForArea(areaIndex).some(
      (note) => this.hasLocalizedValue(note.title) || this.hasLocalizedValue(note.description)
    );
  }

  protected hasLocalizedValue(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    if (value === null || value === undefined || typeof value !== 'object') {
      return false;
    }

    return Object.values(value).some((item) => typeof item === 'string' && item.trim().length > 0);
  }
}
